/**
 * 入场爱心上的取样，做法和原型一致（docs/prototype-v0.1.md）。
 *
 * Taubin 心形曲面 (x² + 9/4·y² + z² − 1)³ = z³(x² + 9/80·y²)，x 横向、y 纵深、z 向上。
 * 改写成 g = (x² + 9/4·y² + z² − 1) − ∛(z³(x² + 9/80·y²))：表面相同，但表面附近近似线性，牛顿投影能稳定收敛。
 * 先在体内均匀取点，再按比例投影到表面，密度均匀；正面看时边缘因为掠射自然更亮，就是粒子爱心的发光轮廓。
 *
 * 每个样本只由种子决定：同一张照片每次打开都落在心上同一个位置。
 */

export interface HeartSample {
  /** 心形局部坐标：x 横向、y 向上、z 纵深，宽高约 ±0.85 */
  x: number;
  y: number;
  z: number;
  /** 0 表面，1 略靠内的柔光，2 内部，3 外圈光尘 */
  kind: number;
  /** 爆炸方向（未归一，长度 0.6–1.4，略偏向水平面） */
  bx: number;
  by: number;
  bz: number;
}

const hg = (x: number, y: number, z: number) => x * x + 2.25 * y * y + z * z - 1 - Math.cbrt(z * z * z * (x * x + 0.1125 * y * y));

function toSurface(q: [number, number, number]): void {
  for (let k = 0; k < 8; k++) {
    const e = 1e-4, v = hg(q[0], q[1], q[2]);
    const gx = (hg(q[0] + e, q[1], q[2]) - hg(q[0] - e, q[1], q[2])) / (2 * e);
    const gy = (hg(q[0], q[1] + e, q[2]) - hg(q[0], q[1] - e, q[2])) / (2 * e);
    const gz = (hg(q[0], q[1], q[2] + e) - hg(q[0], q[1], q[2] - e)) / (2 * e);
    const gg = gx * gx + gy * gy + gz * gz || 1;
    q[0] -= (v * gx) / gg;
    q[1] -= (v * gy) / gg;
    q[2] -= (v * gz) / gg;
  }
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function heartSample(seed: number, out: HeartSample = { x: 0, y: 0, z: 0, kind: 0, bx: 0, by: 0, bz: 0 }): HeartSample {
  const r = mulberry32(seed);
  const q: [number, number, number] = [0, 0, 0];
  do {
    q[0] = (r() * 2 - 1) * 1.25;
    q[1] = (r() * 2 - 1) * 0.8;
    q[2] = r() * 2.5 - 1.2;
  } while (hg(q[0], q[1], q[2]) > 0);
  const u = r();
  let kind: number, k = 1;
  if (u < 0.6) {
    kind = 0;
    toSurface(q);
  } else if (u < 0.78) {
    kind = 1;
    toSurface(q);
    k = 0.94 + r() * 0.04;
  } else if (u < 0.93) {
    kind = 2;
  } else {
    kind = 3;
    toSurface(q);
    k = 1.03 + r() * 0.09;
  }
  // 归一到心形局部坐标：宽约 ±0.85、高约 ±0.84，纵深压扁一些
  out.x = (q[0] * k) / 1.34;
  out.y = (q[2] * k - 0.145) / 1.3;
  out.z = ((q[1] * k) / 1.34) * 0.72;
  out.kind = kind;
  const a = r() * Math.PI * 2, v = r() * 2 - 1, ring = Math.sqrt(1 - v * v), sp = 0.6 + r() * 0.8;
  out.bx = Math.cos(a) * ring * sp;
  out.by = v * sp * 0.6;
  out.bz = Math.sin(a) * ring * sp;
  return out;
}
